import { HttpErrorResponse } from '@angular/common/http';

import { PERRON_POINTS_COPY, gateErrorSingleLineText } from './perron-points-copy';
import { PP_THRESHOLD_FALLBACK } from './perron-points.constants';

/**
 * Gate de Acceso VIP (Perrón Points) en el lado del cliente: resolución del
 * umbral efectivo de un producto y mapeo NEUTRO del 403 del servidor.
 *
 * Neutro = no devuelve `PrepurchaseError`. Nació acoplado a precompra
 * (`prepurchase.model.ts`) y se extrajo aquí para que carrito, checkout y
 * precompra compartan un único parseo del contrato del backend. La restricción
 * dejó de ser cosa de precompras (R7): cualquier producto puede tenerla.
 *
 * El servidor sigue siendo la autoridad — las ocho puertas cortan igual. Esto
 * es la vía primaria de UX (R2) y la lectura del estado bloqueado (R4/R6).
 */

/** Los dos únicos `error` que emite el gate. Nombres congelados por contrato. */
export type EarlyAccessGateCode = 'EA_LOGIN_REQUIRED' | 'EA_INSUFFICIENT_POINTS';

/** Elemento de `blocked_lines[]` del 403 (R10). */
export interface EarlyAccessBlockedLine {
  /** `null` cuando la línea aún no existe (`new-cart-line`) o el flujo no tiene carrito. */
  cartLineId: number | null;
  productId: number;
  productName: string;
  threshold: number;
}

/** Resultado del mapeo del 403, ya con el copy resuelto. */
export interface EarlyAccessGateError {
  /** `null` cuando el 403 no trae un `error` reconocible (API antigua o code inesperado). */
  code: EarlyAccessGateCode | null;
  /** Copy de cara al cliente, personalizado si hay una sola línea bloqueada. */
  message: string;
  /** CTA de acción, solo cuando la salida del cliente es iniciar sesión. */
  ctaLabel: string | null;
  /** Umbral de la línea rectora; `null` si el 403 no lo trae. */
  threshold: number | null;
  /** Saldo del cliente; solo en `EA_INSUFFICIENT_POINTS`. */
  balance: number | null;
  /** Producto de la línea rectora. */
  productId: number | null;
  /** Línea de carrito rectora; `null` cuando aún no existe. */
  cartLineId: number | null;
  /** Todas las líneas que fallan, incluso cuando es una sola. Vacío si el API es antigua. */
  blockedLines: EarlyAccessBlockedLine[];
}

/**
 * Producto (o snapshot de producto de una línea de carrito) del que se puede
 * derivar el umbral. Los tipos admiten `string` porque la API serializa enteros
 * de MySQL como string en algunas vías (regla conocida del repo).
 */
export interface EarlyAccessRestrictable {
  early_access?: boolean | number | string | null;
  early_access_threshold?: number | string | null;
}

/**
 * Corte del CLIENTE (R2): el producto no llegó a salir hacia el API. No lleva
 * mensaje porque no hay nada que mostrar en un toast — el diálogo bloqueante ya
 * está abierto explicando el requisito.
 */
export class EarlyAccessBlockedError extends Error {
  constructor(
    /** Umbral efectivo del producto rechazado. */
    public readonly threshold: number,
    /** Por qué se cortó: sin sesión, sin saldo, o verificación en curso/rota. */
    public readonly reason: EarlyAccessDenialReason,
  ) {
    super('EARLY_ACCESS_BLOCKED');
    this.name = 'EarlyAccessBlockedError';
  }
}

/**
 * Corte del SERVIDOR: una de las ocho puertas devolvió 403. A diferencia del
 * anterior, este SÍ se muestra (el cliente no vio ningún diálogo) y lleva la
 * línea identificada para que el carrito y el checkout la señalen.
 */
export class EarlyAccessGateRejection extends Error {
  constructor(public readonly gate: EarlyAccessGateError) {
    super(gate.message);
    this.name = 'EarlyAccessGateRejection';
  }
}

/* ─── Resolución del umbral efectivo ─────────────────────────────────────── */

/**
 * Umbral efectivo del producto, o `null` si no está restringido.
 *
 * Precedencia (NO TOCAR de la web): `early_access_threshold` del producto →
 * `fallback` (que el caller alimenta con el último umbral conocido del
 * servidor, y en último término con `PP_THRESHOLD_FALLBACK`). El fallback solo
 * entra cuando el producto está marcado `early_access` pero el payload no trae
 * el umbral resuelto — típicamente un JSON cacheado de antes del deploy.
 *
 * `0` es un valor legítimo («solo requiere sesión», R5) y NUNCA se confunde con
 * ausencia: el corte es contra `null`/`undefined`, jamás por falsedad.
 */
export function earlyAccessThresholdOf(
  product: EarlyAccessRestrictable | null | undefined,
  fallback: number = PP_THRESHOLD_FALLBACK,
): number | null {
  if (!product) return null;

  const resolved = toThreshold(product.early_access_threshold);
  if (resolved !== null) return resolved;

  // Sin umbral resuelto en el payload: solo restringimos si el flag lo dice.
  // `!!` porque la vía de modelo crudo entrega TINYINT 0/1, no booleanos.
  return isTruthyFlag(product.early_access) ? Math.max(0, Math.trunc(fallback)) : null;
}

/** `true` si el producto está restringido por Perrón Points (umbral `0` incluido). */
export function isEarlyAccessRestricted(
  product: EarlyAccessRestrictable | null | undefined,
  fallback: number = PP_THRESHOLD_FALLBACK,
): boolean {
  return earlyAccessThresholdOf(product, fallback) !== null;
}

/* ─── Evaluación cliente del gate ────────────────────────────────────────── */

/** Motivo por el que el cliente no puede llevarse el producto. */
export type EarlyAccessDenialReason = 'guest' | 'insufficient' | 'loading' | 'error';

/** Estado del visitante frente al gate, tal y como lo ve el front. */
export interface EarlyAccessViewer {
  authenticated: boolean;
  balance: number;
  /** `true` cuando el saldo ya se resolvió contra el servidor. */
  balanceKnown: boolean;
  /** `true` si el último `/perron-points/me` falló. */
  balanceError: boolean;
}

export type EarlyAccessDecision =
  | { allowed: true; threshold: number | null }
  | { allowed: false; threshold: number; reason: EarlyAccessDenialReason };

/**
 * Decide si el visitante puede llevarse un producto con este umbral. Función
 * pura: el estado de sesión y saldo entra por parámetro (`PointsService` y
 * `AuthService` lo construyen en el borde), de forma que la regla se testea sin
 * TestBed y no se duplica entre el diálogo, el carrito y el checkout.
 *
 * Invariantes de R5, idénticas a las del servidor:
 *  - producto no restringido (`threshold === null`) → pasa siempre;
 *  - umbral `0` + invitado → bloqueo `guest` (el servidor manda `EA_LOGIN_REQUIRED`);
 *  - umbral `0` + cualquier usuario logueado → pasa, aunque tenga saldo `0`;
 *  - `N ≥ 1` → exige `saldo ≥ N`, con el saldo ya resuelto.
 */
export function evaluateEarlyAccess(
  threshold: number | null,
  viewer: EarlyAccessViewer,
): EarlyAccessDecision {
  if (threshold === null) return { allowed: true, threshold: null };
  if (!viewer.authenticated) return { allowed: false, threshold, reason: 'guest' };
  // Umbral 0: tener sesión ES el requisito. Jamás mira el saldo (R5).
  if (threshold <= 0) return { allowed: true, threshold };
  if (viewer.balanceError) return { allowed: false, threshold, reason: 'error' };
  if (!viewer.balanceKnown) return { allowed: false, threshold, reason: 'loading' };
  if (viewer.balance >= threshold) return { allowed: true, threshold };
  return { allowed: false, threshold, reason: 'insufficient' };
}

/* ─── Mapeo del 403 ──────────────────────────────────────────────────────── */

/**
 * Mapea el 403 del gate a un error neutro con el copy ya resuelto. Devuelve
 * `null` cuando la respuesta no es un 403 (el caller sigue con su enrutado
 * normal).
 *
 * Tolera el contrato ANTIGUO (solo `error`/`code`/`threshold`/`balance`): las
 * claves aditivas de R10 son opcionales y su ausencia deja `blockedLines`
 * vacío, sin degradar el mensaje.
 */
export function mapEarlyAccessGate403(err: unknown): EarlyAccessGateError | null {
  if (!(err instanceof HttpErrorResponse) || err.status !== 403) return null;

  const body = (err.error ?? {}) as Record<string, unknown>;
  const code = readGateCode(body['error']);
  const blockedLines = readBlockedLines(body['blocked_lines']);
  const governing = governingLine(blockedLines);

  const threshold = toThreshold(body['threshold']) ?? governing?.threshold ?? null;
  const balance = toThreshold(body['balance']);
  const productId = toThreshold(body['product_id']) ?? governing?.productId ?? null;
  const cartLineId = toThreshold(body['cart_line_id']) ?? governing?.cartLineId ?? null;

  return {
    code,
    message: gateMessage(code, blockedLines),
    ctaLabel: code === 'EA_LOGIN_REQUIRED' ? PERRON_POINTS_COPY.gateError.loginRequiredCta : null,
    threshold,
    balance: code === 'EA_INSUFFICIENT_POINTS' ? balance : null,
    productId,
    cartLineId,
    blockedLines,
  };
}

/**
 * Copy del 403. Con UNA sola línea bloqueada se personaliza con su nombre y
 * umbral; con varias (o con umbral `0`, donde «0+ Perrón Points» no diría
 * nada) manda el mensaje por código.
 */
function gateMessage(
  code: EarlyAccessGateCode | null,
  lines: EarlyAccessBlockedLine[],
): string {
  const copy = PERRON_POINTS_COPY.gateError;
  if (lines.length === 1 && lines[0].threshold >= 1) {
    return gateErrorSingleLineText(lines[0].productName, lines[0].threshold);
  }
  if (code === 'EA_LOGIN_REQUIRED') return copy.loginRequired;
  if (code === 'EA_INSUFFICIENT_POINTS') return copy.insufficientPoints;
  return copy.fallback;
}

/**
 * Línea rectora = la de mayor umbral; empate → la primera en orden de entrada
 * (el backend ya las manda ordenadas por `cart_lines.id` ascendente, y este
 * `>` estricto conserva ese orden).
 */
function governingLine(lines: EarlyAccessBlockedLine[]): EarlyAccessBlockedLine | null {
  return lines.reduce<EarlyAccessBlockedLine | null>(
    (best, line) => (best === null || line.threshold > best.threshold ? line : best),
    null,
  );
}

function readGateCode(raw: unknown): EarlyAccessGateCode | null {
  if (raw === 'EA_LOGIN_REQUIRED' || raw === 'EA_INSUFFICIENT_POINTS') return raw;
  return null;
}

function readBlockedLines(raw: unknown): EarlyAccessBlockedLine[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const line = item as Record<string, unknown>;
    const productId = toThreshold(line['product_id']);
    const threshold = toThreshold(line['threshold']);
    if (productId === null || threshold === null) return [];
    return [
      {
        cartLineId: toThreshold(line['cart_line_id']),
        productId,
        productName: typeof line['product_name'] === 'string' ? line['product_name'] : '',
        threshold,
      },
    ];
  });
}

/**
 * Entero no negativo a partir de un número o un string numérico (la API
 * serializa enteros como string en algunas vías). `null` para todo lo demás —
 * incluidos `null`, `undefined` y `''` — para que la AUSENCIA nunca se
 * confunda con el `0`.
 */
function toThreshold(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? Math.max(0, Math.trunc(raw)) : null;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : null;
  }
  return null;
}

function isTruthyFlag(raw: unknown): boolean {
  if (typeof raw === 'string') return raw !== '' && raw !== '0' && raw !== 'false';
  return !!raw;
}
